import type * as THREE from 'three';
import type { EnemyDefinition } from '../../shared/game/world-types.js';
import { createUnitVisual } from './entities/unit-visual.js';
import { IdlePreparation } from './idle-preparation.js';
import { releaseObjects } from './release-objects.js';

type Visual = ReturnType<typeof createUnitVisual>;
/** Two spares per summoner, up to this many per template. */
const MOST = 8;

/**
 * What a figure is built from: a summoned enemy is its template copied, then given its own id, spawn and side, and a browser never
 * sees what Assess reveals.
 */
function figureKey(definition: EnemyDefinition) {
  const { id: _id, name: _name, spawn: _spawn, factionId: _faction, summoner: _summoner, encounterId: _encounter, encounterDifficulty: _difficulty, profile, ...rest } = definition;
  const { resistances: _resistances, weaknesses: _weaknesses, ...shown } = profile;
  return JSON.stringify({ ...rest, profile: shown });
}

/**
 * Figures for enemies summoned mid-fight, built while the scene prepares: a Goblin figure takes 150–300 ms to build, which stalled the
 * frame each time a Taskmaster called a Minion. A spare given out is replaced only when the page is idle, so a fight never builds one.
 * Templates come from the bundled content; a summon whose figure differs from every template is built as before.
 */
export class SummonSpares {
  private spares = new Map<string, { template: EnemyDefinition; visuals: Visual[] }>();
  private refills = new Set<IdlePreparation<EnemyDefinition>>();
  private serial = 0;
  constructor(private scene: THREE.Scene, private templates: Record<string, EnemyDefinition>) {}

  /** Builds spares for every template these enemies can summon, and for `calls`, what heroes can summon (two per caller each). */
  prepare(enemies: readonly EnemyDefinition[], calls: readonly EnemyDefinition[] = []) {
    const wanted = new Map<EnemyDefinition, number>();
    for (const template of calls) wanted.set(template, Math.min(MOST, (wanted.get(template) ?? 0) + 2));
    for (const enemy of enemies) for (const trait of enemy.profile.traits ?? []) {
      const template = trait.id === 'summon-after-attack' ? this.templates[trait.template] : undefined;
      if (template) wanted.set(template, Math.min(MOST, (wanted.get(template) ?? 0) + 2));
    }
    for (const [template, count] of wanted) for (let i = this.spares.get(figureKey(template))?.visuals.length ?? 0; i < count; i++) this.build(template);
  }

  /** A ready figure for this summoned enemy, or nothing if none was built from its template. */
  take(definition: EnemyDefinition): Visual | undefined {
    const entry = this.spares.get(figureKey(definition)), visual = entry?.visuals.pop();
    if (!entry || !visual) return undefined;
    visual.group.removeFromParent();
    const refill = new IdlePreparation([entry.template], template => { this.build(template); this.refills.delete(refill); });
    this.refills.add(refill); refill.start();
    return visual;
  }

  dispose() {
    for (const refill of this.refills) refill.cancel();
    for (const { visuals } of this.spares.values()) for (const visual of visuals) { visual.animator.dispose(); visual.group.removeFromParent(); releaseObjects(visual.group); }
    this.refills.clear(); this.spares.clear();
  }

  private build(template: EnemyDefinition) {
    const key = figureKey(template), entry = this.spares.get(key) ?? { template, visuals: [] };
    // Hidden in the scene, so warming compiles and uploads it with everything else.
    const visual = createUnitVisual({ ...template, id: `summon-spare-${++this.serial}` });
    visual.group.visible = false; this.scene.add(visual.group);
    entry.visuals.push(visual); this.spares.set(key, entry);
  }
}
